import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ListItem, UnorderedList } from '@chakra-ui/react';

import {
  TaxForecastProfileMainActivityType,
  TaxForecastProfileSubActivityType,
} from '__generated__/GQL';
import Accordion from 'components/_core/Accordion';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { UpdateTaxDataDocument } from 'features/Accounting/pages/Estimation/graphql/updateTaxData.gql';

import StepLayout from '../../layout/Step';
import { StepComponentProps } from '../types';
import CompanyDetails from './components/CompanyDetails';
import ReglementedLiberal, {
  IsReglemented,
} from './components/ReglementedLiberalCard';
import SubActivityCard from './components/SubActivityCard';
import { useCompanyDetails } from './hooks/useCompanyDetails';
import { locales } from './locales';

type SavePayload = {
  isReglementedLiberal: boolean | null;
  mainActivityType: TaxForecastProfileMainActivityType;
  subActivity: TaxForecastProfileSubActivityType | null;
};

const CompanySituation: FC<StepComponentProps<SavePayload>> = ({
  context,
  onBack,
  onNext,
  onSave,
}) => {
  const { showToast } = useToast();
  const { stepProgress, taxForecastProfile } = context;
  const { missingData } = useCompanyDetails();

  // Init state with user's data or default values used on the official estimator tool
  const [selectedActivityType, setSelectedActivityType] =
    useState<TaxForecastProfileMainActivityType>(
      taxForecastProfile?.mainActivityType ??
        TaxForecastProfileMainActivityType.Commercial,
    );
  const [selectedSubActivityType, setSelectedSubActivityType] =
    useState<TaxForecastProfileSubActivityType>(
      taxForecastProfile?.subActivity ??
        TaxForecastProfileSubActivityType.Selling,
    );
  const [isReglementedLiberal, setIsReglementedLiberal] =
    useState<IsReglemented>(
      taxForecastProfile?.isReglementedLiberal
        ? IsReglemented.YES
        : IsReglemented.NO,
    );

  const [updateTaxData, { loading: updateTaxDataLoading }] = useMutation(
    UpdateTaxDataDocument,
    { onError: () => showToast({ message: locales.error, type: 'error' }) },
  );

  const handleSubmit = () => {
    const input: SavePayload = {
      isReglementedLiberal: null,
      mainActivityType: selectedActivityType,
      subActivity: selectedSubActivityType,
    };

    if (selectedActivityType === TaxForecastProfileMainActivityType.Liberal) {
      input.subActivity = null;
      input.isReglementedLiberal =
        isReglementedLiberal === IsReglemented.YES ? true : false;
    }

    updateTaxData({
      onCompleted: () => {
        onSave(input);
        onNext();
      },
      variables: {
        input: {
          companyProfileId: context.companyProfile.companyProfileId,
          taxForecastProfileData: input,
          taxFrequency: context.companyProfile.taxFrequency,
        },
      },
    });
  };

  return (
    <StepLayout>
      <StepLayout.Header onBack={onBack} stepProgress={stepProgress} />

      <StepLayout.Body>
        <Heading marginBottom="space-32">{locales.title}</Heading>

        <CompanyDetails />

        <Text
          fontWeight="weight-500"
          marginBottom="space-16"
          size="lg"
          variant="primary"
        >
          {locales.activityType.title}
        </Text>
        <SunshineCard.RadioGroup
          onChange={(value) => {
            setSelectedActivityType(
              value as TaxForecastProfileMainActivityType,
            );
          }}
          value={selectedActivityType}
        >
          {selectedActivityType !== TaxForecastProfileMainActivityType.Craft ? (
            <SunshineCard
              title={locales.activityType.craft.label}
              value={TaxForecastProfileMainActivityType.Craft}
              variant="radio"
            />
          ) : (
            <SubActivityCard
              onChange={(value) => setSelectedSubActivityType(value)}
              parent={{
                title: locales.activityType.craft.label,
                value: TaxForecastProfileMainActivityType.Craft,
              }}
              value={selectedSubActivityType}
            />
          )}

          {selectedActivityType !==
          TaxForecastProfileMainActivityType.Commercial ? (
            <SunshineCard
              title={locales.activityType.commercial.label}
              value={TaxForecastProfileMainActivityType.Commercial}
              variant="radio"
            />
          ) : (
            <SubActivityCard
              onChange={(value) => setSelectedSubActivityType(value)}
              parent={{
                title: locales.activityType.commercial.label,
                value: TaxForecastProfileMainActivityType.Commercial,
              }}
              value={selectedSubActivityType}
            />
          )}

          {selectedActivityType !==
          TaxForecastProfileMainActivityType.Liberal ? (
            <SunshineCard
              title={locales.activityType.liberal.label}
              value={TaxForecastProfileMainActivityType.Liberal}
              variant="radio"
            />
          ) : (
            <ReglementedLiberal
              onChange={(value) => setIsReglementedLiberal(value)}
              value={isReglementedLiberal}
            />
          )}
        </SunshineCard.RadioGroup>
      </StepLayout.Body>

      <StepLayout.Sidebar>
        <Heading marginBottom="space-16">{locales.sidebar.title}</Heading>

        <Accordion allowMultiple allowToggle={undefined}>
          <Accordion.Item title={locales.sidebar.craft.question}>
            <Accordion.Text>{locales.sidebar.craft.subtitle}</Accordion.Text>
            <Accordion.Text>
              <UnorderedList>
                <ListItem>{locales.sidebar.craft.criteria1}</ListItem>
                <ListItem>{locales.sidebar.craft.criteria2}</ListItem>
                <ListItem>{locales.sidebar.craft.criteria3}</ListItem>
              </UnorderedList>
            </Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.sidebar.commercial.question}>
            <Accordion.Text>
              {locales.sidebar.commercial.subtitle}
            </Accordion.Text>
            <Accordion.Text>
              <UnorderedList>
                <ListItem>{locales.sidebar.commercial.criteria1}</ListItem>
                <ListItem>{locales.sidebar.commercial.criteria2}</ListItem>
                <ListItem>{locales.sidebar.commercial.criteria3}</ListItem>
                <ListItem>{locales.sidebar.commercial.criteria4}</ListItem>
              </UnorderedList>
            </Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.sidebar.liberal.question}>
            <Accordion.Text>{locales.sidebar.liberal.subtitle}</Accordion.Text>
            <Accordion.Text>
              <UnorderedList>
                <ListItem>{locales.sidebar.liberal.criteria1}</ListItem>
                <ListItem>{locales.sidebar.liberal.criteria2}</ListItem>
                <ListItem>{locales.sidebar.liberal.criteria3}</ListItem>
              </UnorderedList>
            </Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.sidebar.reglementedLiberal.question}>
            <Accordion.Text>
              {locales.sidebar.reglementedLiberal.firstCriteria}
            </Accordion.Text>
            <Accordion.Text>
              {locales.sidebar.reglementedLiberal.secondCriteria}
            </Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.sidebar.unreglementedLiberal.question}>
            <Accordion.Text>
              {locales.sidebar.unreglementedLiberal.firstCriteria}
            </Accordion.Text>
            <Accordion.Text>
              {locales.sidebar.unreglementedLiberal.secondCriteria}
            </Accordion.Text>
          </Accordion.Item>
        </Accordion>
      </StepLayout.Sidebar>

      <StepLayout.Footer stepProgress={stepProgress}>
        <StepLayout.FooterButton
          isDisabled={missingData.length > 0}
          isLoading={updateTaxDataLoading}
          onClick={handleSubmit}
        />
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default CompanySituation;
